import { PageHero } from '@components/common';
import { Target, FileCheck, ArrowRight, Clock, Users, Shield } from 'lucide-react';
import { CALENDLY_URL } from '@constants';

const idealFor = [
  'Organisations preparing for investment or acquisition',
  'Companies needing to understand their current security baseline',
  'Leadership teams seeking independent validation of security posture',
  'Businesses preparing for compliance certifications (ISO 27001, SOC 2)',
  'Boards requiring objective security assessment',
  'Companies post-merger integrating security functions',
];

const deliverables = [
  {
    title: 'Maturity Assessment Report',
    description: 'Detailed evaluation against NIST CSF or ISO 27001 controls',
  },
  {
    title: 'Gap Analysis',
    description: 'Clear identification of control gaps with risk ratings',
  },
  {
    title: 'Remediation Roadmap',
    description: 'Prioritised action plan with timelines and resource estimates',
  },
  {
    title: 'Executive Presentation',
    description: 'Board-ready materials explaining findings and recommendations',
  },
  {
    title: 'Risk Register',
    description: 'Documented security risks with treatment recommendations',
  },
];

const process = [
  {
    step: '01',
    title: 'Discovery',
    description: 'Stakeholder interviews, documentation review, and scope confirmation',
    duration: '2-3 days',
  },
  {
    step: '02',
    title: 'Assessment',
    description: 'Evaluation against chosen framework across people, process, and technology',
    duration: '1 week',
  },
  {
    step: '03',
    title: 'Analysis',
    description: 'Gap identification, risk prioritisation, and roadmap development',
    duration: '3-5 days',
  },
  {
    step: '04',
    title: 'Reporting',
    description: 'Draft report review, refinement, and final presentation',
    duration: '2-3 days',
  },
];

const frameworks = [
  {
    name: 'NIST Cybersecurity Framework',
    description: 'US government standard focusing on Identify, Protect, Detect, Respond, Recover',
  },
  {
    name: 'ISO 27001',
    description: 'International standard for information security management systems',
  },
];

export default function SecurityMaturityPage() {
  const packageSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Security Maturity Assessment',
    description:
      'Understand your current security posture with an independent evaluation against industry frameworks',
    url: 'https://thecisonetwork.com/packages/security-maturity',
    provider: {
      '@type': 'Organization',
      name: 'The CISO Network',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(packageSchema) }}
      />
      <PageHero
        title="Security Maturity Assessment"
        subtitle="Understand your current security posture with an independent evaluation against industry frameworks"
        eyebrow="Our Packages"
        seoTitle="Security Maturity Assessment | The CISO Network"
        seoDescription="Comprehensive security maturity assessment against NIST or ISO 27001. Clear roadmap, board-ready report, and prioritised remediation plan."
      />

      {/* Overview */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Know Where You Stand
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Many organisations struggle to answer a simple question: how secure are we?
                  Without an objective baseline, it is impossible to prioritise investments,
                  communicate with the board, or measure progress over time.
                </p>
                <p>
                  Our Security Maturity Assessment provides an independent, comprehensive evaluation
                  of your security posture against recognised industry frameworks. You receive a
                  clear understanding of your current state, identified gaps with risk ratings, and
                  a prioritised roadmap for improvement.
                </p>
                <p>
                  Whether you are preparing for investment, planning a compliance journey, or simply
                  need visibility into your security position, this assessment delivers the clarity
                  you need.
                </p>
              </div>
            </div>
            <div className="bg-background rounded-2xl p-8 border border-border">
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="h-12 w-12 rounded-xl bg-teal flex items-center justify-center">
                  <Target className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground">At a Glance</h3>
              </div>
              <div className="space-y-4 mb-6">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Duration</span>
                  <span className="font-medium text-foreground">2-3 weeks</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Framework</span>
                  <span className="font-medium text-foreground">NIST or ISO 27001</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Deliverables</span>
                  <span className="font-medium text-foreground">Report + Roadmap</span>
                </div>
              </div>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-hero w-full justify-center"
              >
                Discuss This Package <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Ideal For */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Who This Is For
            </h2>
          </div>
          <div className="flex justify-center">
            <ul className="space-y-4 inline-block text-left">
              {idealFor.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-muted-foreground">
                  <Users className="h-5 w-5 text-teal flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Frameworks */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Assessment Frameworks
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Choose the framework that best aligns with your industry and objectives
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {frameworks.map((fw, i) => (
              <div key={i} className="bg-background rounded-2xl p-8 border border-border">
                <div className="h-12 w-12 rounded-xl bg-teal/10 flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-teal" />
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground mb-2">{fw.name}</h3>
                <p className="text-muted-foreground">{fw.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              What You Receive
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {deliverables.map((d, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-border">
                <div className="h-10 w-10 rounded-xl bg-teal flex items-center justify-center mb-4">
                  <FileCheck className="h-5 w-5 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-bold text-foreground mb-2">{d.title}</h3>
                <p className="text-muted-foreground text-sm">{d.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              The Assessment Process
            </h2>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {process.map((step, i) => (
                <div key={i} className="flex gap-6">
                  <div className="flex-shrink-0">
                    <div className="h-14 w-14 rounded-xl bg-teal flex items-center justify-center">
                      <span className="text-lg font-bold text-accent-foreground">{step.step}</span>
                    </div>
                  </div>
                  <div className="flex-grow pb-8 border-b border-border last:border-0">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-2">
                      <h3 className="text-xl font-heading font-bold text-foreground">
                        {step.title}
                      </h3>
                      <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        {step.duration}
                      </span>
                    </div>
                    <p className="text-muted-foreground">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Ready to Assess Your Security Posture?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Book a discovery call to discuss your needs and confirm this package is right for you.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Call <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
